import { Check, FileText } from '@phosphor-icons/react';

import { CircleNotch } from '@phosphor-icons/react';
import { Conversation, Tool } from '../../../../@types/IA.type';
import { useState, useEffect } from 'react';
import STORAGE_KEYS from '../../../../shared/constants/storage';
import { ChatMessage } from '../../../../@types/IA.type';
import { useStorage } from '../../../../shared/utils/general.util';
import {
	sendEscalationForm,
	updateMessageChatAI,
} from '../../../../services/heru-ai.service';

const EscalationFormTool = ({
	tool,
	userProfile,
	message,
	conversation,
}: {
	tool: Tool;
	userProfile: any;
	message: ChatMessage;
	conversation: Conversation;
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [problem, setProblem] = useState('');
	const [isSended, setIsSended] = useState(false);
	const previousEscalationForms =
		JSON.parse(useStorage()?.getItem(STORAGE_KEYS.ESCALATION_FORM) || '[]') ||
		[];

	const alreadySended =
		previousEscalationForms.find(
			(form: any) => form.messageId === message?.uuid,
		) || isSended;

	const updateMessage = async () => {
		try {
			if (!conversation?.id || !message?.uuid) {
				throw new Error('Thread ID or message UUID is missing');
			}
			await updateMessageChatAI({
				threadId: conversation?.id,
				messageUUID: message?.uuid,
				field: 'toolsExecuted',
				value: [tool?.name],
			});
		} catch (error) {
			throw error;
		}
	};

	const handleSubmit = async (
		e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>,
	) => {
		try {
			e.preventDefault();

			if (!problem || alreadySended) {
				return;
			}
			setIsLoading(true);

			const date = new Date()
				.toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })
				.split(',')[0];
			const time = new Date()
				.toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })
				.split(',')[1];

			const emailBody = `<h1>Escalaciones AI Heru (Web App)</h1>
            <b>Información del usuario:</b>
            <ul>
                <li>Nombre: ${userProfile?.first_name} ${userProfile?.father_surname}</li>
                <li>Email: ${userProfile?.email}</li>
                <li>Teléfono: ${userProfile?.cellphone}</li>
                <li>Fecha: ${date} - ${time}</li>
            </ul>
            
            <b>Problema:</b>
            <p>${problem}</p>
            
            <b>Conversación:</b>
            <ul>
                ${conversation.thread.map((t) => `<li><b>${t.role}:</b> ${t.content}</li>`).join('\n')}
            </ul>`;

			const body = {
				subject: `Ayuda escalacion AI - ${userProfile?.email} - ${date} ${time}`,
				html_content: emailBody,
			};

			await sendEscalationForm(body);
			await updateMessage();

			useStorage()?.setItem(
				STORAGE_KEYS.ESCALATION_FORM,
				JSON.stringify([
					...previousEscalationForms,
					{
						problem,
						messageId: message?.uuid,
					},
				]),
			);
			setIsSended(true);
		} catch (error) {
			throw error;
		} finally {
			setIsLoading(false);
		}
	};

	const extractConversationSummary = (content: string | undefined) => {
		if (!content) return '';

		const match = content.match(/conversationSummary=([^&]+)/);
		return match ? decodeURIComponent(match[1]).replaceAll('+', ' ') : '';
	};

	const problemSummary = extractConversationSummary(message?.content);

	useEffect(() => {
		if (problemSummary) {
			setProblem(problemSummary);
		}
	}, [problemSummary]);

	useEffect(() => {
		if (message?.toolsExecuted?.includes(tool?.name)) {
			setIsSended(true);
		}
	}, [message]);

	return (
		<form
			onSubmit={handleSubmit}
			className='max-w-[94%] md:max-w-[70%] shadow p-2 rounded-xl flex flex-col gap-4 border-[6px] border-h-primary-90'
		>
			<div className='flex gap-2 items-center'>
				<div className=' text-h-dark  w-fit rounded-lg'>
					<FileText
						className='min-w-[18px] min-h-[18px]'
						weight='duotone'
					/>
				</div>
				<p className='font-regular text-base'>Formulario de soporte</p>
			</div>
			{alreadySended ? (
				<div className='flex flex-col gap-1'>
					<div className='font-light opacity-50'>{alreadySended?.problem}</div>
					<hr className='my-1' />
					<p className='font-medium'>
						Tu mensaje fue enviado exitosamente. Un agente de soporte te
						contactará pronto a tu correo electrónico{' '}
						<span className='bg-h-primary-90/50'>{userProfile?.email}</span>.
					</p>
				</div>
			) : (
				<div className='font-light flex flex-col gap-1'>
					<label htmlFor='name'>Descripción del problema</label>
					<textarea
						rows={8}
						id='problem'
						required
						placeholder='Ej. No puedo acceder a mi cuenta, no puedo realizar un pago, etc.'
						className='text-base md:text-sm outline-none w-full bg-transparent border border-h-neutral-90 rounded p-1 border-'
						value={problem}
						onChange={(e) => setProblem(e.target.value)}
					/>
				</div>
			)}
			{alreadySended ? (
				<button className='h-9 flex gap-2 items-center justify-center border border-h-neutral-90 text-h-neutral-30 rounded-lg p-2'>
					Formulario enviado{' '}
					<Check
						className='min-w-[14px]'
						weight='bold'
					/>
				</button>
			) : (
				<button
					className='bg-h-primary text-white rounded-lg p-2 w-full flex gap-2 items-center justify-center h-9 disabled:cursor-not-allowed'
					type='submit'
					disabled={isLoading || !problem}
					onClick={handleSubmit}
				>
					{isLoading ? (
						<CircleNotch
							className='animate-spin text-white size-5'
							weight='bold'
						/>
					) : (
						'Enviar'
					)}
				</button>
			)}
		</form>
	);
};

export default EscalationFormTool;
